import { useState } from "react";
import { professionalPublicTakePath } from "../../config/navConfig";
import { useAlertContext } from "../../context/AlertContext";

const formatMinutes = (minutes) => {
    const value = Number(minutes);
    if (!Number.isFinite(value)) return "нет данных";
    if (value < 60) return `${Math.max(1, Math.round(value))} мин`;
    return `${Math.floor(value / 60)} ч ${Math.round(value % 60)} мин`;
};

/** Правка №1/36: цифры по тесту одной строкой; клик открывает «Результаты тестов» с фильтром. */
function TestStatsPanel({ stats, onOpen }) {
    const chip = (label, value, tab, tone = "text-slate-900") => (
        <button
            type="button"
            onClick={() => onOpen?.(tab)}
            title="Открыть результаты по этому тесту"
            className="inline-flex items-baseline gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-slate-100"
        >
            <span className="text-[11px] text-slate-500">{label}</span>
            <span className={`text-xs font-bold tabular-nums ${tone}`}>{value ?? "—"}</span>
        </button>
    );
    return (
        <div className="-mx-1.5 mt-2 flex flex-wrap gap-x-0.5 gap-y-0.5" data-testid={`test-stats-${stats.test_id}`}>
            {chip("Отправлено", stats.sent)}
            {chip("Пройдено", stats.completed)}
            {chip("Успешно", stats.passed, null, "text-emerald-700")}
            {chip("Не засчитан", stats.failed)}
            {stats.awaiting_hr ? chip("Ждут HR", stats.awaiting_hr) : null}
            {chip("Нарушения", stats.violations, "violations", stats.violations ? "text-rose-700" : "text-slate-900")}
            {stats.suspicious ? chip("Подозрит.", stats.suspicious, "suspicious", "text-amber-700") : null}
            {chip("Ср. балл", stats.avg_score_percent != null ? `${stats.avg_score_percent}%` : null)}
            {chip("Время", stats.median_minutes != null ? formatMinutes(stats.median_minutes) : null)}
        </div>
    );
}

export default function TestList({tests, onTestDeleted, onTestClick, onTestEdit, psychCatalog, stats, onOpenResults}) {
    const { showAlert } = useAlertContext();
    const [copiedId, setCopiedId] = useState(null);
    const isUniversal = (t) => t.test_category === "universal";
    const professionalTests = (tests || []).filter(t => t.test_type !== "Психологический тест" && !isUniversal(t));
    const universalTests = (tests || []).filter(isUniversal);
    // Правка №36: сверху — тесты с активными вакансиями, без них — внизу.
    const activeVacancies = (t) => stats?.[t.id]?.active_vacancies || [];
    const statsReady = Boolean(stats && Object.keys(stats).length);
    const withVacancies = statsReady ? professionalTests.filter((t) => activeVacancies(t).length > 0) : professionalTests;
    const withoutVacancies = statsReady ? professionalTests.filter((t) => activeVacancies(t).length === 0) : [];
    const legacyPsych = (tests || []).filter(t => t.test_type === "Психологический тест");

    const copyLink = async (testId) => {
        const path = professionalPublicTakePath(testId);
        const url = `${window.location.origin}${path}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(testId);
            showAlert("Ссылка скопирована", "success");
            showAlert(
                "Это общая ссылка без привязки к кандидату. Лучше отправлять тест из карточки кандидата — тогда результат сразу попадёт в карточку.",
                "warning"
            );
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            showAlert(`Скопируйте вручную: ${url}`, "warning");
        }
    };

    if ((!tests || tests.length === 0) && !psychCatalog) {
        return (
            <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200/60 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                </div>
                <h3 className="text-lg font-medium text-slate-800">Тестов пока нет</h3>
                <p className="text-slate-500 mt-1">Создайте новый тест или сгенерируйте с помощью ИИ.</p>
            </div>
        );
    }

    const renderTestGroup = (groupTests, title, { muted = false } = {}) => (
        <div className="mb-6 last:mb-0">
            {title && (
                <div className="mb-3 flex items-center gap-3">
                    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                        {groupTests.length}
                    </span>
                </div>
            )}

            {groupTests.length === 0 ? (
                <p className="py-3 text-sm italic text-slate-500">Нет тестов в этой категории.</p>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {groupTests.map(test => (
                        <div key={test.id} className={`group flex flex-col rounded-xl border border-slate-200/70 px-4 py-3 shadow-sm transition hover:shadow-md ${muted ? "bg-slate-50/80 opacity-80 hover:opacity-100" : "bg-white"}`}>
                            <div className="flex items-start justify-between gap-2">
                                <h3 className="min-w-0 truncate text-sm font-bold leading-5 text-slate-900 group-hover:text-blue-600" title={test.name}>{test.name}</h3>
                                <div className="flex shrink-0 items-center gap-1">
                                    {test.test_type === "url" && (
                                        <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">По ссылке</span>
                                    )}
                                    {test.test_type === "Психологический тест" && (
                                        <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Психологический</span>
                                    )}
                                    {test.publication_status === "draft" && (
                                        <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Черновик</span>
                                    )}
                                    {test.has_unpublished_changes && (
                                        <span className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">Есть изменения</span>
                                    )}
                                </div>
                            </div>

                            {!isUniversal(test) && (() => {
                                const vacancies = activeVacancies(test);
                                if (!vacancies.length) {
                                    return <p className="mt-1 text-xs text-slate-400">Нет активных вакансий</p>;
                                }
                                return (
                                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`test-vacancies-${test.id}`}>
                                        {vacancies.slice(0, 3).map((vacancy) => (
                                            <span key={vacancy.id} className="max-w-full truncate rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-800" title={vacancy.name}>
                                                {vacancy.name}
                                            </span>
                                        ))}
                                        {vacancies.length > 3 && (
                                            <span className="px-1 py-0.5 text-[11px] text-slate-500" title={vacancies.slice(3).map((vacancy) => vacancy.name).join(", ")}>
                                                +{vacancies.length - 3}
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}

                            {stats?.[test.id] && !isUniversal(test) && (
                                <TestStatsPanel stats={stats[test.id]} onOpen={(tab) => onOpenResults?.(test, tab)} />
                            )}

                            <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                                <span className="truncate text-[11px] text-slate-400">
                                    {test.created_at ? `Создан ${new Date(test.created_at).toLocaleDateString("ru-RU")}` : ""}
                                </span>
                                <div className="flex shrink-0 items-center gap-0.5">
                                    {onOpenResults && (
                                        <button
                                            type="button"
                                            onClick={() => onOpenResults(test)}
                                            data-testid={`open-results-${test.id}`}
                                            className="mr-1 rounded-md px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
                                        >
                                            Результаты
                                        </button>
                                    )}
                                    {test.test_type === "Вопросно-ответная форма" && test.publication_status !== "draft" && (
                                        <button
                                            type="button"
                                            onClick={() => copyLink(test.id)}
                                            data-testid={`copy-prof-link-${test.id}`}
                                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                                            title={copiedId === test.id ? "Скопировано" : "Скопировать общую ссылку (кандидат сам вводит ФИО — лучше отправлять из карточки кандидата)"}
                                            aria-label="Скопировать ссылку"
                                        >
                                            {copiedId === test.id ? <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> : <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
                                        </button>
                                    )}
                                    {test.url && (
                                        <a href={test.url} target="_blank" rel="noopener noreferrer" className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900" title="Внешняя ссылка" aria-label="Внешняя ссылка"><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></a>
                                    )}
                                    <button type="button" onClick={() => onTestClick(test)} className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900" title="Просмотр" aria-label="Просмотр"><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg></button>
                                    {onTestEdit && (
                                        <button type="button" onClick={() => onTestEdit(test)} className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900" title="Редактировать" aria-label="Редактировать"><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                                    )}
                                    <button type="button" onClick={() => onTestDeleted(test.id)} className="flex h-7 w-7 items-center justify-center rounded-md text-rose-400 transition-colors hover:bg-rose-50 hover:text-rose-700" title="Удалить" aria-label="Удалить"><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div>
            {renderTestGroup(withVacancies, "Профессиональные тесты")}
            <div className="mb-8">
                <div className="mb-3 flex items-center gap-3">
                    <h2 className="text-lg font-bold text-slate-900">Универсальные тестирования</h2>
                </div>
                <p className="-mt-2 mb-4 text-sm text-slate-500">Проходят все кандидаты: психологический тест и анкета кандидата.</p>
                {psychCatalog}
                {universalTests.length > 0 && (
                    <div className="mt-6">{renderTestGroup(universalTests, null)}</div>
                )}
                {legacyPsych.length > 0 && (
                    <div className="mt-6">{renderTestGroup(legacyPsych, null)}</div>
                )}
            </div>
            {withoutVacancies.length > 0 && renderTestGroup(withoutVacancies, "Без активных вакансий", { muted: true })}
        </div>
    );
}
